import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { tagHotjarRecording } from '../../helpers/util'
import useAuth from '../../hooks/useAuth'
import useRequest from '../../hooks/useRequest'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import TabPanel from '../../components/TabPanel/TabPanel'
import { formatRequestsListTable } from '../../helpers/tableUtils'
import useFilterModal from '../../hooks/useFilterModal'
import PlannedMaintenanceTable from './PlannedMaintenanceTable'
import { getInitialPMFilters } from '../../helpers/requestsFilters'
import { parse } from 'qs'
import {
    savePMWorkOrders,
    getPMWorkOrdersByType,
} from '../../../../indexedDb/pmWorkOrders'
import { useNetworkStatus } from '../../utils/networkStatus'
import { useFetchState } from '../../context/FetchStateContext'
import { useLocation } from 'react-router-dom'

const PlannedMaintenancePage = () => {
    const {
        closedPMRequests,
        openPMRequests,
        getPMRequests,
        pmsFilters,
        needsUpdatePM,
        changeNeedsUpdatePM,
    } = useRequest()

    const [filterOpen, setFilterOpen] = useState(false)
    const { user } = useAuth()
    const locPrefs = user?.locationPreferences
    const { isOffline } = useNetworkStatus()
    const { setIsFetching, setDataFetched } = useFetchState()
    const location = useLocation()
    const filterByStatus = location.state?.filterByStatus

    const windowParams = useMemo(
        () => parse(window.location.search, { ignoreQueryPrefix: true }),
        [window.location.search],
    )

    // Always use fresh filters - don't persist between navigations
    const allFilters = getInitialPMFilters(
        locPrefs,
        windowParams,
        'activeTabFilters',
    )

    const allPastFilters = getInitialPMFilters(
        locPrefs,
        windowParams,
        'pastTabFilters',
    )

    // Use local state for current session filters (not persisted store)
    // State resets when component unmounts on navigation
    const [activeFilters, setActiveFilters] = useState(allFilters)
    const [pastFilters, setPastFilters] = useState(allPastFilters)

    // const filtersToUse = useMemo(() => {
    //     const base = getInitialPMFilters(locPrefs, windowParams)
    //     if (hasParams) {
    //         // Respect filters coming from URL
    //         return base
    //     }
    //     // Disable all filters when no URL params present
    //     return base?.map(group => ({
    //         ...group,
    //         filters: group?.filters?.map(f => ({ ...f, checked: false })) || [],
    //     }))
    // }, [locPrefs, windowParams, hasParams])

    const tab = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.activeTab

    const firstTab = {
        key: tab ? tab : 'activeRequests',
        active: true,
    }

    const fetchPMWorkOrders = useCallback(async () => {
        try {
            const storedOpenPMWorkOrders = await getPMWorkOrdersByType('openPM')
            const storedClosedPMWorkOrders = await getPMWorkOrdersByType(
                'closedPM',
            )

            if (storedOpenPMWorkOrders.length) {
                const filteredOpenPMRequests = storedOpenPMWorkOrders.filter(
                    request =>
                        locPrefs.some(
                            location =>
                                String(location.id) ===
                                String(request.shipToId),
                        ),
                )
                setFilterOpenPMRequests(
                    formatRequestsListTable(filteredOpenPMRequests, 'openPM'),
                )
            }

            if (storedClosedPMWorkOrders.length) {
                let filteredClosedPMRequests = storedClosedPMWorkOrders.filter(
                    request =>
                        locPrefs.some(
                            location =>
                                String(location.id) ===
                                String(request.shipToId),
                        ),
                )

                // Apply status filter from navigation state
                if (filterByStatus) {
                    filteredClosedPMRequests = filteredClosedPMRequests.filter(
                        request => request.status === filterByStatus,
                    )
                }

                setFilterClosedPMRequests(
                    formatRequestsListTable(
                        filteredClosedPMRequests,
                        'closedPM',
                    ),
                )
            }

            setIsFetching(false)
            setDataFetched(true)
        } catch (error) {
            console.error(
                'Error fetching PM Work Orders from IndexedDB:',
                error,
            )
        }
        setIsFetching(false)
        setDataFetched(true)
    }, [isOffline, filterByStatus])

    // Update filters when location preferences change (async load)
    useEffect(() => {
        if (locPrefs?.length > 0) {
            const freshActiveFilters = getInitialPMFilters(
                locPrefs,
                windowParams,
                'activeTabFilters',
            )
            const freshPastFilters = getInitialPMFilters(
                locPrefs,
                windowParams,
                'pastTabFilters',
            )
            setActiveFilters(freshActiveFilters)
            setPastFilters(freshPastFilters)
        }
    }, [locPrefs])

    useEffect(() => {
        if (
            (openPMRequests?.length === 0 && pmsFilters?.length === 0) ||
            needsUpdatePM
        ) {
            setIsFetching(true)
            const locationPreferencesIds = locPrefs.map(item => `${item?.id}`)
            getPMRequests(locationPreferencesIds, false)
            changeNeedsUpdatePM({ plannedMaintenance: false })
        }
        fetchPMWorkOrders()
    }, [])

    const {
        filteredData: filteredOpenPMRequests,
        onFilter: onFilterOpenPMRequests,
        setFilteredData: setFilterOpenPMRequests,
    } = useFilterModal(formatRequestsListTable, openPMRequests)

    const {
        filteredData: filteredClosedPMRequests,
        onFilter: onFilterClosedPMRequests,
        setFilteredData: setFilterClosedPMRequests,
    } = useFilterModal(formatRequestsListTable, closedPMRequests)

    useEffect(() => {
        tagHotjarRecording('Planned Maintenance List')
    }, [])

    useEffect(() => {
        if (openPMRequests?.length > 0) {
            const formattedOpenPMRequests = formatRequestsListTable(
                openPMRequests,
                'openPM',
            )
            setFilterOpenPMRequests(formattedOpenPMRequests)
            savePMWorkOrders(formattedOpenPMRequests, 'openPM')
        }
    }, [openPMRequests, isOffline])

    useEffect(() => {
        if (closedPMRequests?.length > 0) {
            let closedToFormat = closedPMRequests

            // Apply status filter from navigation state
            if (filterByStatus) {
                closedToFormat = closedPMRequests.filter(
                    request => request.status === filterByStatus,
                )
            }

            const formattedClosedPMRequests = formatRequestsListTable(
                closedToFormat,
                'closedPM',
            )
            setFilterClosedPMRequests(formattedClosedPMRequests)
            savePMWorkOrders(
                formatRequestsListTable(closedPMRequests, 'closedPM'),
                'closedPM',
            )
        }
    }, [closedPMRequests, isOffline, filterByStatus])

    useEffect(() => {
        if (activeFilters?.length > 0 && openPMRequests?.length > 0) {
            onFilterOpenPMRequests(activeFilters)
        }
        if (pastFilters?.length > 0 && closedPMRequests?.length > 0) {
            onFilterClosedPMRequests(pastFilters)
        }
    }, [openPMRequests, closedPMRequests])

    const handleApplyOpenFilters = useCallback(
        filters => {
            setActiveFilters(filters)
            onFilterOpenPMRequests(filters)
        },
        [onFilterOpenPMRequests],
    )

    const handleApplyClosedFilters = useCallback(
        filters => {
            setPastFilters(filters)
            onFilterClosedPMRequests(filters)
        },
        [onFilterClosedPMRequests],
    )

    const memoTableActive = useMemo(
        () => (
            <PlannedMaintenanceTable
                filtersToUse={activeFilters}
                data={filteredOpenPMRequests}
                onFilter={handleApplyOpenFilters}
                title="Planned Maintenance Details"
                tabFilters="activeTabFilters"
                subtitle={
                    <span>
                        You have a total of{' '}
                        <b className="text-black90">
                            {filteredOpenPMRequests?.length}
                        </b>{' '}
                        active PMs
                    </span>
                }
            />
        ),
        [
            filteredOpenPMRequests,
            filterOpen,
            activeFilters,
            handleApplyOpenFilters,
        ],
    )

    const memoTableClosed = useMemo(
        () => (
            <PlannedMaintenanceTable
                filtersToUse={pastFilters}
                data={filteredClosedPMRequests}
                onFilter={handleApplyClosedFilters}
                title="Past Planned Maintenance Details"
                tabFilters="pastTabFilters"
                subtitle={
                    <span>
                        You have a total of{' '}
                        <b className="text-black90">
                            {filteredClosedPMRequests?.length}
                        </b>{' '}
                        past PMs
                    </span>
                }
            />
        ),
        [
            filteredClosedPMRequests,
            filterOpen,
            pastFilters,
            handleApplyClosedFilters,
        ],
    )

    const handleTabChange = () => {
        setFilterOpen(false)
        fetchPMWorkOrders()
    }

    const renderTabs = useCallback(() => {
        return [
            {
                title: 'Active PMs',
                content: memoTableActive,
                key: 'activeRequests',
                hasPadding: false,
            },
            {
                title: 'Past PMs',
                content: memoTableClosed,
                key: 'pastRequests',
                hasPadding: false,
            },
        ]
    }, [
        filteredOpenPMRequests,
        filteredClosedPMRequests,
        memoTableActive,
        memoTableClosed,
    ])

    return (
        <ContentLayout>
            <MainHeading title="Planned Maintenance" />
            <TabPanel
                firstTab={firstTab}
                onChangeTab={handleTabChange}
                background="bg-white"
                tabs={renderTabs()}
            />
        </ContentLayout>
    )
}

export default PlannedMaintenancePage
